import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const LINKS = [
  { label: 'Who We Are', to: '/who-we-are' },
  { label: 'What We Do', to: '/what-we-do' },
  { label: 'Our Work', to: '/what-we-have-done' },
  { label: 'Team', to: '/team' },
  { label: 'Contact', to: '/contact-us' },
];

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // On non-home pages, keep nav opaque since the hero is shorter.
  const transparent = isHome && !scrolled;

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border] duration-500 ${
      transparent ? 'bg-transparent' : 'bg-paper/85 backdrop-blur-xl border-b border-border'
    }`}>
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-20 lg:h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src="/assets/rccd-emblem.png"
            alt=""
            aria-hidden="true"
            className="h-10 sm:h-11 lg:h-12 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.04]"
          />
          <div className="flex flex-col leading-none">
            <span className={`font-display text-xl sm:text-2xl tracking-[0.05em] transition-colors duration-500 ${transparent ? 'text-paper' : 'text-ink'}`}>
              RCCD
            </span>
            <span className={`mono text-[9px] sm:text-[10px] uppercase tracking-[0.18em] mt-1 transition-colors duration-500 ${transparent ? 'text-paper/60' : 'text-muted-foreground'}`}>
              <span className="text-clay">A Distinguished</span> Company
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map(l => {
            const baseClasses = `mono text-[11px] uppercase tracking-[0.2em] transition-colors underline-grow pb-0.5 ${
              transparent ? 'text-paper/85 hover:text-primary-glow' : 'text-foreground/80 hover:text-clay'
            }`;
            const activeClass = transparent ? '!text-primary-glow' : '!text-clay';
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace(/\//g, '')}`}
                className={({ isActive }) => `${baseClasses} ${isActive ? activeClass : ''}`}
              >
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 lg:gap-3">
          <a href="tel:7146374405" className={`hidden md:flex items-center gap-2 mono text-xs hover:text-clay transition-colors ${transparent ? 'text-paper/90' : 'text-ink'}`}>
            <Phone className="h-3.5 w-3.5" />
            (714) 637-4405
          </a>
          <Button asChild className={`hidden sm:inline-flex h-10 rounded-none px-5 mono text-[11px] uppercase tracking-[0.2em] transition-colors ${transparent ? 'bg-paper text-ink hover:bg-primary-glow' : 'bg-ink text-paper hover:bg-clay'}`}>
            <Link to="/contact-us">Get in Touch</Link>
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-trigger" variant="ghost" size="icon" className={`lg:hidden ${transparent ? 'text-paper' : 'text-ink'}`}>
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-ink border-l-0 text-paper p-0 w-full sm:max-w-md">
              <SheetTitle className="sr-only">RCCD navigation menu</SheetTitle>
              <SheetDescription className="sr-only">Navigate to Who We Are, What We Do, What We Have Done, or Contact</SheetDescription>
              <div className="flex items-center justify-between px-6 h-20 border-b border-paper/10">
                <div className="flex items-center gap-3">
                  <img src="/assets/rccd-emblem.png" alt="" aria-hidden="true" className="h-9 w-auto object-contain" />
                  <div className="flex flex-col leading-none">
                    <span className="font-display text-xl text-paper tracking-[0.05em]">RCCD</span>
                    <span className="mono text-[9px] uppercase tracking-[0.18em] mt-1 text-paper/55">Menu</span>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="text-paper/80" aria-label="Close menu"><X /></button>
              </div>
              <nav className="flex flex-col p-6 gap-1">
                {LINKS.map((l, i) => (
                  <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                     className="flex items-baseline justify-between py-4 border-b border-paper/10 group">
                    <span className="font-display text-3xl text-paper group-hover:text-primary-glow transition-colors">{l.label}</span>
                    <span className="mono text-[10px] text-paper/40">{String(i + 1).padStart(2, '0')}</span>
                  </Link>
                ))}
                <Link to="/contact-us" onClick={() => setOpen(false)} className="mt-8 inline-flex items-center justify-center bg-paper text-ink py-4 mono text-[11px] uppercase tracking-[0.2em]">
                  Get in Touch
                </Link>
                <a href="tel:7146374405" className="mt-3 inline-flex items-center justify-center gap-2 border border-paper/20 py-4 mono text-[11px] uppercase tracking-[0.2em] text-paper/80">
                  <Phone className="h-3.5 w-3.5" /> (714) 637-4405
                </a>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
